/**
 * TopPostsFeed — Feb 2026 · Phase 84.30
 *
 * Shared component rendered on both the Marketing project view AND on the
 * Dashboard's new "Top posts" tab. Aggregates real posts across every
 * connected channel via `GET /api/marketing/top-posts`, sorted by engagement.
 *
 * Each row shows: channel icon · date/time · trending arrow · engagement
 * counts · "Open post" link. Users can filter by channel.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Instagram, Facebook, TrendingUp, TrendingDown, Minus, ExternalLink, Heart, MessageCircle, Share2, Loader2 } from "lucide-react";

const CHANNEL_META = {
  facebook:  { label: "Facebook",  color: "#1877F2", Icon: Facebook },
  instagram: { label: "Instagram", color: "#E1306C", Icon: Instagram },
};

function fmtDateTime(iso) {
  if (!iso) return "";
  return new Intl.DateTimeFormat("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true }).format(new Date(iso));
}
function fmtCount(n) {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 10_000) return `${(v / 1000).toFixed(1)}k`;
  if (v >= 1000) return `${(v / 1000).toFixed(2)}k`;
  return String(v);
}

export default function TopPostsFeed({ limit = 12 }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [generatedAt, setGeneratedAt] = useState(null);

  useEffect(() => {
    let live = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/marketing/top-posts?limit=${limit}`);
        if (live) { setRows(data?.items || []); setGeneratedAt(data?.generated_at); }
      } catch { if (live) setRows([]); }
      finally { if (live) setLoading(false); }
    })();
    return () => { live = false; };
  }, [limit]);

  const filtered = useMemo(() => rows.filter((r) => filter === "all" ? true : r.channel === filter), [rows, filter]);
  const channels = useMemo(() => Array.from(new Set(rows.map((r) => r.channel))), [rows]);

  return (
    <div data-testid="top-posts-feed">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="label-overline">Top posts · live</div>
          <div className="font-display text-lg font-bold text-slate-900 mt-0.5">Sorted by engagement — click any card to open</div>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          <button onClick={() => setFilter("all")} className={`text-[11px] px-2 py-1 rounded-sm ${filter === "all" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid="top-posts-filter-all">
            All ({rows.length})
          </button>
          {channels.map((c) => {
            const meta = CHANNEL_META[c] || { label: c, color: "#475569", Icon: ExternalLink };
            const count = rows.filter((r) => r.channel === c).length;
            return (
              <button key={c} onClick={() => setFilter(c)} className={`text-[11px] px-2 py-1 rounded-sm inline-flex items-center gap-1 ${filter === c ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid={`top-posts-filter-${c}`}>
                <meta.Icon size={11} /> {meta.label} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="card-flat p-8 text-center text-slate-400 text-xs" data-testid="top-posts-loading">
          <Loader2 size={16} className="animate-spin mx-auto mb-2" />
          Fetching latest posts from your connected channels…
        </div>
      ) : filtered.length === 0 ? (
        <div className="card-flat p-8 text-center text-slate-400 text-xs" data-testid="top-posts-empty">
          No posts found. Once your team publishes, they&apos;ll appear here automatically.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((p) => <PostCard key={`${p.channel}-${p.id}`} post={p} />)}
        </div>
      )}
      {generatedAt && (
        <div className="text-[10px] text-slate-400 italic mt-3">Data pulled live from Meta Graph API · updated {fmtDateTime(generatedAt)}</div>
      )}
    </div>
  );
}

function PostCard({ post }) {
  const meta = CHANNEL_META[post.channel] || { label: post.channel, color: "#475569", Icon: ExternalLink };
  const Icon = meta.Icon;
  const Trend = post.trend === "up" ? TrendingUp : post.trend === "down" ? TrendingDown : Minus;
  const trendCls = post.trend === "up" ? "text-emerald-600" : post.trend === "down" ? "text-red-600" : "text-slate-400";
  const trendLabel = post.trend === "up" ? "Trending up" : post.trend === "down" ? "Below avg" : "Steady";
  return (
    <a
      href={post.permalink || "#"}
      target="_blank"
      rel="noreferrer noopener"
      className="card-flat p-3 hover:border-slate-400 transition-colors block group"
      data-testid={`top-post-${post.channel}-${post.id}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-white px-1.5 py-0.5 rounded-sm" style={{ background: meta.color }}>
          <Icon size={11} /> {meta.label}
        </span>
        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold ${trendCls}`} title={trendLabel} data-testid={`top-post-trend-${post.channel}-${post.id}`}>
          <Trend size={11} /> {trendLabel}
        </span>
      </div>
      {post.media_url && (
        <img src={post.media_url} alt="" className="w-full aspect-square object-cover rounded-sm mb-2 border border-slate-100" />
      )}
      <p className="text-xs text-slate-800 line-clamp-3 mb-2 min-h-[36px]">{post.text || "(no caption)"}</p>
      <div className="text-[10px] text-slate-500 mb-2">{fmtDateTime(post.posted_at)}</div>
      <div className="flex items-center gap-3 text-[11px] text-slate-600 border-t border-slate-100 pt-2">
        <span className="inline-flex items-center gap-1" title="Reactions"><Heart size={11} /> {fmtCount(post.likes)}</span>
        <span className="inline-flex items-center gap-1" title="Comments"><MessageCircle size={11} /> {fmtCount(post.comments)}</span>
        {post.shares > 0 && <span className="inline-flex items-center gap-1" title="Shares"><Share2 size={11} /> {fmtCount(post.shares)}</span>}
        <span className="ml-auto text-slate-400 inline-flex items-center gap-1 group-hover:text-[#F54800]">
          Open <ExternalLink size={9} />
        </span>
      </div>
    </a>
  );
}
